import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button, Spin, Tabs, message } from 'antd';
import intl from 'react-intl-universal';

import CustomDrawer, { IdrawerRef } from '@/components/CustomDrawer';
import GuestDetailDrawer, { IGuestDetailRef } from '@/pages/guest/component/GuestDetailDrawer';
import MemberCancelCheckButton from '@/pages/guest/component/MemberCancelCheckButton';
import { formatAreaMobileStr } from '@/components/BaseMobileInput/helper';
import { fetchTakeCareDetail, fetchTakeCareHistory, fetchUpdateTakeCare } from '@/api/takeCare';
import { SubOrderStatus } from '@/config';
import TakeCareTabItem, { TakeCareTabItemRef } from '../TakeCareTabItem';
import HistoryTabItem from '../HistoryTabItem';
import styles from './index.module.scss';

export type TakeCareDrawerRef = {
  openDrawer: (values: { recNo: string; callback: () => void }) => void;
};

const TakeCareDrawer = React.forwardRef<TakeCareDrawerRef, any>((props, ref) => {
  const [active, setActive] = useState<any>('takeCareDetail');

  const [detail, setDetail] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);

  const [historyList, setHistoryList] = useState<Array<any>>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);

  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  const drawerRef = useRef<IdrawerRef>();
  const paramsRef = useRef<{ recNo?: string; callback?: () => void }>({});

  const careRef = useRef<TakeCareTabItemRef>();
  const guestDetailRef = useRef<IGuestDetailRef>();

  const getTakeCareDetail = async (data) => {
    if (data.recNo) {
      try {
        setLoading(true);
        const res = await fetchTakeCareDetail(data);
        if (res.code === 0) {
          setDetail(res.data || {});
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    }
  };

  const getTakeCareHistory = async (data) => {
    if (data?.advanceCareId) {
      try {
        setHistoryLoading(true);
        const res = await fetchTakeCareHistory(data);
        if (res.code === 0) {
          setHistoryList(res.data || []);
        }
        setHistoryLoading(false);
      } catch (error) {
        setHistoryLoading(false);
      }
    }
  };

  const resetState = () => {
    paramsRef.current = null;
    setActive('takeCareDetail');
    setDetail({});
    setLoading(false);
    setHistoryList([]);
    setHistoryLoading(false);
    setSaveLoading(false);
  };

  const openDrawer = (values) => {
    resetState();
    drawerRef.current.openDrawer();
    paramsRef.current = values;
    getTakeCareDetail(values);
  };

  const onTabsChange = (key) => {
    setActive(key);
    if (key === 'takeCareDetail') {
      const { recNo } = paramsRef.current;
      getTakeCareDetail({ recNo });
    }
    if (key === 'takeCareHistory') {
      getTakeCareHistory({ advanceCareId: detail?.id });
    }
  };

  const handleSave = async () => {
    careRef.current.getSubmitValues().then(async (values) => {
      try {
        setSaveLoading(true);
        const { id, recNo, resvNo } = detail || {};
        const res = await fetchUpdateTakeCare({ ...values, id, recNo, resvNo });
        if (res.code === 0) {
          message.success(intl.get('roomstate_take_care_edit_modal_save_success'));
          drawerRef.current.closeDrawer();
          const { callback } = paramsRef.current;
          if (callback) {
            callback();
          }
        }
        setSaveLoading(false);
      } catch (error) {
        setSaveLoading(false);
      }
    });
  };

  const handleClose = () => {
    drawerRef.current.closeDrawer();
  };

  const handleOpenGuestDetail = (wyuid) => {
    guestDetailRef.current.openDrawer({ guestRecord: { wyuid } });
  };

  const handleCloseGuestDetailDrawer = () => {
    const { recNo } = paramsRef.current;
    getTakeCareDetail({ recNo });
  };

  useEffect(() => {
    return () => {
      resetState();
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openDrawer,
    }),
    [],
  );

  const personInfo = detail?.masterWyuId
    ? {
        wyuid: detail?.masterWyuId,
        name: detail?.masterName,
        mobile: detail?.masterMobile,
      }
    : { wyuid: detail?.wyuId, name: detail?.name, mobile: detail?.mobile };

  return (
    <>
      <CustomDrawer
        ref={drawerRef}
        className={styles.takeCareDrawer}
        title={intl.get('roomstate_take_care_edit_modal_title')}
        footer={
          <>
            {active === 'takeCareDetail' && detail?.roomStatus === SubOrderStatus.Reservation ? (
              <Button type="primary" loading={saveLoading} onClick={handleSave}>
                {intl.get('roomstate_take_care_edit_modal_btn_submit')}
              </Button>
            ) : null}
            <Button type="ghost" onClick={handleClose}>
              {intl.get('roomstate_take_care_edit_modal_btn_cancel')}
            </Button>
          </>
        }
      >
        <div className={styles.container}>
          <GuestDetailDrawer ref={guestDetailRef} onClose={handleCloseGuestDetailDrawer} />

          <Spin spinning={loading}>
            <div className={styles.info}>
              <div>
                <div className={styles.label}>
                  {intl.get('roomstate_take_care_edit_modal_info_guest')}
                </div>
                <div>{personInfo?.name}</div>
              </div>
              <div>
                <div className={styles.label}>
                  {intl.get('roomstate_take_care_edit_modal_info_mobile')}
                </div>
                <div>{formatAreaMobileStr(personInfo?.mobile)}</div>
              </div>
              <div>
                <div className={styles.label}>
                  {intl.get('roomstate_take_care_edit_modal_info_ota')}
                </div>
                <div>{detail?.otaOrderNo || '-'}</div>
              </div>
              <div>
                <div className={styles.label}>{intl.get('help_store_address_type_email')}</div>
                <div>{detail?.email || '-'}</div>
              </div>

              {personInfo?.wyuid ? (
                <div style={{ marginLeft: 'auto', marginRight: '8px' }}>
                  <MemberCancelCheckButton
                    type="link"
                    businessDate={detail?.expectedDepartureTime}
                    text={intl.get('roomstate_take_care_edit_modal_btn_guest_detail')}
                    wyuid={personInfo?.wyuid}
                    onClick={() => handleOpenGuestDetail(personInfo?.wyuid)}
                  />
                </div>
              ) : null}
            </div>
          </Spin>

          <Tabs
            activeKey={active}
            onChange={onTabsChange}
            type="card"
            items={[
              {
                label: intl.get('roomstate_take_care_edit_modal_tab_detail'),
                key: 'takeCareDetail',
                children: <TakeCareTabItem ref={careRef} detail={detail} loading={loading} />,
              },
              {
                label: intl.get('roomstate_take_care_edit_modal_tab_record'),
                key: 'takeCareHistory',
                children: <HistoryTabItem list={historyList} loading={historyLoading} />,
              },
            ]}
          />
        </div>
      </CustomDrawer>
    </>
  );
});

export default TakeCareDrawer;
